import { FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { EnergyUsagePurposeSelector } from '@eneco/sitecore-components-content/AddressFinder/EnergyUsagePurpose/EnergyUsagePurposeSelector';
import { Stack, Text, NotificationBox } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { SummaryStepRendering } from '@sitecore/types/SummaryStep';
import { Control } from 'react-hook-form';
import * as yup from 'yup';

import {
  TermsAndConditions,
  SummaryContractNotifications,
  SummaryPaymentDetails,
  SummaryPersonalDetailsOverview,
  SummaryProductsOverview,
  SummaryStepError,
} from '../../../shared/components';
import { useSummaryStepLogic } from '../../../shared/hooks';
import { PriceDetailsOverview } from '../../components';

interface FormFields {
  iban?: string;
  energyUsagePurpose?: string;
  [key: string]: unknown;
}

export const SummaryRetentionStep: FC = () => {
  const { SummaryStep: { fields: textualData } = {} } = usePlaceholderContent<{
    SummaryStep: SummaryStepRendering;
  }>();

  const {
    label,
    flowContext,
    sendToFlowMachine,
    isLoading,
    isSuccessful,
    isFailed,
    isNotFound,
    isNotAvailable,
    isNotAuthenticated,
    isOrderFailed,
    selectedOffer,
    selectedOfferTermsAndConditions,
    selectedCashbackTexts,
    textualDataKey,
    form,
  } = useSummaryStepLogic<FormFields>({
    extensionMode: 'always',
    paymentDetailsTextualData: textualData?.paymentDetails,
    promotionTexts: textualData?.promotionTexts,
    extraSchemaFields: {
      energyUsagePurpose: yup
        .string()
        .required(textualData?.energyUsage?.energyUsagePurposeList?.value?.requiredMessage ?? ''),
    },
    shouldBlockSubmit: values => values.energyUsagePurpose === 'business',
  });

  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors, isSubmitSuccessful },
  } = form;

  const { isPersonalDetailsEditProhibited } = flowContext;

  const energyUsagePurpose = watch('energyUsagePurpose');
  const isBusinessSelected = energyUsagePurpose === 'business';

  return (
    <Layout.Content variant="B" isLoading={isLoading} handleSubmit={handleSubmit}>
      {isSuccessful && (
        <>
          <Header>
            <Header.Title>{textualData?.content?.title?.value}</Header.Title>
            {textualData?.content?.text?.value && <Header.Text>{textualData.content.text.value}</Header.Text>}
          </Header>

          {/* TODO: Replace hardcoded energyUsagePurposeList with Sitecore textualData when available */}
          <EnergyUsagePurposeSelector
            control={control as Control<{ energyUsagePurpose?: string; [key: string]: unknown }>}
            energyUsagePurposeList={textualData?.energyUsage?.energyUsagePurposeList}
            direction="row"
          />

          {isBusinessSelected && textualData?.energyUsage?.businessUsagePurposeNotification?.value?.content && (
            <NotificationBox
              isAlert={false}
              variant={textualData?.energyUsage?.businessUsagePurposeNotification?.value?.variant ?? 'info'}
              title={textualData?.energyUsage?.businessUsagePurposeNotification?.value?.title}
              text={<RichText html={textualData?.energyUsage?.businessUsagePurposeNotification?.value?.content} />}
            />
          )}

          {textualData?.content?.notification?.value?.content && (
            <NotificationBox
              isAlert={false}
              variant={textualData?.content?.notification?.value?.variant}
              title={textualData?.content?.notification?.value?.title}
              text={<RichText html={textualData?.content?.notification?.value?.content} />}
            />
          )}

          <SummaryContractNotifications
            selectedOffer={selectedOffer}
            notifications={textualData?.notifications}
            skipVariablePricingCheck
          />

          <Stack.Item>
            <SummaryPersonalDetailsOverview
              textualData={textualData?.personalDetailsOverview}
              flowContext={flowContext}
              onEditPersonalDetails={() => sendToFlowMachine({ type: 'GOTO', stepName: 'STEP_PERSONAL_DETAILS' })}
              onEditAddress={() => sendToFlowMachine({ type: 'GOTO', stepName: 'STEP_ADDRESS' })}
              onEditMoving={() => sendToFlowMachine({ type: 'GOTO', stepName: 'STEP_MOVING' })}
            />
          </Stack.Item>

          <Stack.Item>
            {selectedOffer && (
              <SummaryProductsOverview
                textualData={textualData}
                offer={selectedOffer}
                flowContext={flowContext}
                textualDataKey={textualDataKey}
                selectedCashbackTexts={selectedCashbackTexts}
                cashbackValue={Number(selectedOffer?.costDiscounts?.cashback?.value ?? 0)}
                cashbackType={selectedOffer?.costDiscounts?.cashback?.type}
                priceDetails={
                  <PriceDetailsOverview flowContext={flowContext} textualData={textualData} offer={selectedOffer} />
                }
              />
            )}
          </Stack.Item>

          {!isPersonalDetailsEditProhibited && (
            <SummaryPaymentDetails
              textualData={textualData?.paymentDetails}
              register={register}
              ibanError={errors.iban}
            />
          )}

          <TermsAndConditions
            label={label}
            flowContext={flowContext}
            textualData={textualData}
            offer={selectedOffer}
            offerTermsAndConditions={selectedOfferTermsAndConditions}
          />

          <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>

          {textualData?.content?.energyDisputeCommitteeText?.value && (
            <Text size="BodyXS" color="textLowEmphasis">
              {textualData?.content?.energyDisputeCommitteeText?.value}
            </Text>
          )}
        </>
      )}

      {(isFailed || isOrderFailed) && (
        <SummaryStepError
          isNotAuthenticated={isNotAuthenticated}
          isNotAvailable={isNotAvailable}
          isNotFound={isNotFound}
          isOrderFailed={isOrderFailed}
          textualData={textualData?.genericError}
        />
      )}
    </Layout.Content>
  );
};
